import { createContext, useContext } from 'react';
import { hu } from './locales/hu';
import { apiErrors } from './locales/apiErrors';
export type Language = 'hu' | 'en';

export function translateApiError(language: Language, code?: string, message?: string) {
  if (code && apiErrors[code]) return apiErrors[code][language];
  if (language === 'hu' && code?.startsWith('missing_')) return 'Hiányzó kötelező mező.';
  if (language === 'hu' && code?.startsWith('invalid_')) return 'Érvénytelen adat. Ellenőrizze a megadott értékeket.';
  if (message)
    return language === 'hu'
      ? (hu[message] ?? 'A művelet sikertelen. Ellenőrizze az adatokat, majd próbálja újra.')
      : message;
  return language === 'hu' ? 'Váratlan hiba történt.' : 'An unexpected error occurred.';
}

export const I18nContext = createContext<{ language: Language; t: (key: string) => string }>({
  language: 'en',
  t: (k: string) => k,
});
export function useI18n() {
  return useContext(I18nContext);
}
export function translate(language: Language, key: string) {
  return language === 'hu' ? (hu[key] ?? key) : key;
}

// Legacy import previews return readable messages rather than codes. Translate their
// fixed templates while keeping row numbers, external keys and customer names intact.
export function translateValidationMessage(language: Language, message: string): string {
  if (language === 'en') return message;
  const row = /^Row (\d+) \(([^)]+)\): (.*)$/.exec(message);
  const prefix = row ? `${row[1]}. sor (${row[2]}): ` : '';
  const text = row?.[3] ?? message;
  const patterns: [RegExp, (...parts: string[]) => string][] = [
    [/^Duplicate external_key '(.*)'\.$/, (key) => `Ismétlődő external_key: '${key}'.`],
    [/^Invalid external_key '(.*)'\.$/, (key) => `Érvénytelen external_key: '${key}'.`],
    [
      /^Location '(.*)' requires a 1–64 character name\.$/,
      (key) => `A(z) '${key}' helyszín neve 1–64 karakter legyen.`,
    ],
    [
      /^Location '(.*)' has invalid node_type '(.*)'\.$/,
      (key, type) => `A(z) '${key}' helyszín node_type értéke érvénytelen: '${type}'.`,
    ],
    [/^Location '(.*)' has invalid status\.$/, (key) => `A(z) '${key}' helyszín állapota érvénytelen.`],
    [
      /^Location '(.*)' has unknown parent '(.*)'\.$/,
      (key, parent) => `A(z) '${key}' helyszín szülője nem található: '${parent}'.`,
    ],
    [/^Location '(.*)' creates a cycle\.$/, (key) => `A(z) '${key}' helyszín körkörös hivatkozást hoz létre.`],
    [/^Location '(.*)' exceeds 12 levels\.$/, (key) => `A(z) '${key}' helyszín meghaladja a 12 szintet.`],
    [
      /^Moving '(.*)' also moves its subtree\.$/,
      (name) => `A(z) '${name}' mozgatása az alárendelt helyszíneket is mozgatja.`,
    ],
    [
      /^restore the deleted location explicitly before importing\.$/,
      () => 'Importálás előtt állítsa vissza a törölt helyszínt.',
    ],
    [/^hierarchy contains a cycle\.$/, () => 'A helyszínstruktúra körkörös hivatkozást tartalmaz.'],
    [/^unknown parent '(.*)'\.$/, (key) => `A szülőhelyszín nem található: '${key}'.`],
    [/^parent is deleted\.$/, () => 'A szülőhelyszín törölve van.'],
    [/^hierarchy exceeds 12 parent links\.$/, () => 'A helyszínstruktúra meghaladja a 12 szülőkapcsolatot.'],
  ];
  for (const [pattern, render] of patterns) {
    const match = pattern.exec(text);
    if (match) return prefix + render(...match.slice(1));
  }
  return prefix + 'Az import ellenőrzése hibát jelzett. Ellenőrizze a fájl szerkezetét és adatait.';
}
